import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Eye, Shield, Check, Briefcase } from "lucide-react";
import { useLocation } from "wouter";
import { useState, useEffect } from "react";

export function HeroSection() {
    const [, setLocation] = useLocation();
    const [mounted, setMounted] = useState(false);

    // Final target values
    const targets = {
        photos: 2547382,
        time: 25,
        editors: 547,
        satisfaction: 98,
    };

    // Start with final values for SSR, will animate after mount
    const [stats, setStats] = useState(targets);

    useEffect(() => {
        setMounted(true);

        // Reset to 0 and animate on client-side only
        setStats({
            photos: 0,
            time: 0,
            editors: 0,
            satisfaction: 0,
        });

        const duration = 2000; // 2 seconds
        const steps = 60;
        const interval = duration / steps;

        let step = 0;
        const timer = setInterval(() => {
            step++;
            const progress = step / steps;

            setStats({
                photos: Math.floor(targets.photos * progress),
                time: Math.floor(targets.time * progress),
                editors: Math.floor(targets.editors * progress),
                satisfaction: Math.floor(targets.satisfaction * progress),
            });

            if (step >= steps) clearInterval(timer);
        }, interval);

        return () => clearInterval(timer);
    }, []);

    return (
        <section className="relative pt-24 pb-20 md:pt-48 md:pb-32 overflow-hidden">
            <div className="absolute inset-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background pointer-events-none" />

            {/* Animated Glow Orbs */}
            <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/30 rounded-full blur-3xl opacity-50 animate-pulse-subtle pointer-events-none" />
            <div className="absolute bottom-20 right-1/4 w-64 h-64 bg-purple-500/30 rounded-full blur-3xl opacity-50 animate-pulse-subtle delay-1000 pointer-events-none" />

            <div className="container relative z-10 mx-auto px-4 text-center">
                <Badge variant="outline" className="mb-6 px-4 py-1.5 text-sm border-primary/30 bg-primary/10 text-primary backdrop-blur-sm">
                    ✨ AI-Powered Magic
                </Badge>

                <h1 className="text-4xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-8">
                    Professional Photo Edits <br />
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-500 to-primary bg-300% animate-gradient">
                        in 30 Seconds
                    </span>
                </h1>

                <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
                    Transform your photos instantly with our <span className="text-foreground font-medium">Conversational AI</span>. Simply describe your edits in plain text. <span className="text-foreground font-medium">No subscriptions</span>—just <span className="text-foreground font-medium">pay per download</span>. Need perfection? Get a <span className="text-foreground font-medium">human editor backup</span> instantly.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-600">
                    <Button size="lg" className="h-14 px-8 text-lg rounded-full shadow-lg shadow-primary/25 hover:shadow-primary/50 transition-all duration-300 hover:scale-105" onClick={() => setLocation("/create")}>
                        Start Editing Free
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button size="lg" variant="outline" className="h-14 px-8 text-lg rounded-full border-primary/20 hover:bg-primary/5 backdrop-blur-sm" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}>
                        How it Works
                    </Button>
                </div>

                <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm text-muted-foreground animate-in fade-in duration-1000 delay-1000">
                    <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-primary" />
                        <span>Free previews</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-primary" />
                        <span>100% Private</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>No subscriptions</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-primary" />
                        <span>Commercial rights</span>
                    </div>
                </div>

                {/* Live Stats Bar */}
                <div className="pt-16">
                    <div className="backdrop-blur-2xl bg-gradient-to-r from-white/10 to-white/5 border border-white/20 rounded-3xl p-6 md:p-8 shadow-2xl">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="text-center">
                                <div className="text-xl sm:text-2xl md:text-4xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent mb-1">
                                    {stats.photos.toLocaleString()}+
                                </div>
                                <div className="text-sm text-muted-foreground font-medium">Photos Edited</div>
                            </div>
                            <div className="text-center">
                                <div className="text-xl sm:text-2xl md:text-4xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent mb-1">
                                    {stats.time}sec
                                </div>
                                <div className="text-sm text-muted-foreground font-medium">AI Processing</div>
                            </div>
                            <div className="text-center">
                                <div className="text-xl sm:text-2xl md:text-4xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent mb-1">
                                    {stats.editors}+
                                </div>
                                <div className="text-sm text-muted-foreground font-medium">Pro Editors</div>
                            </div>
                            <div className="text-center">
                                <div className="text-xl sm:text-2xl md:text-4xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent mb-1">
                                    {stats.satisfaction}%
                                </div>
                                <div className="text-sm text-muted-foreground font-medium">Satisfaction Rate</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
